import { gradeOverview } from "./grades.js";
import { classroomPerformance } from "./performance.js";
import { compareStudentNumbers } from "./roster.js";
import type {
  AcademicTerm,
  AttendanceRecord,
  CalendarEvent,
  DailyRecord,
  HomeroomHomeworkRecord,
  HomeroomRecord,
  HomeworkAssignment,
  HomeworkRecord,
  Student,
  StudentRoutineItem,
} from "./types.js";

export function academicYearTerms(year: number, count: 2 | 3): AcademicTerm[] {
  const schoolYear = `${year}–${year + 1}`;
  const ranges =
    count === 2
      ? [
          [`${year}-09-01`, `${year + 1}-01-31`],
          [`${year + 1}-02-01`, `${year + 1}-08-31`],
        ]
      : [
          [`${year}-09-01`, `${year}-12-31`],
          [`${year + 1}-01-01`, `${year + 1}-04-30`],
          [`${year + 1}-05-01`, `${year + 1}-08-31`],
        ];
  return ranges.map(([startDate, endDate], index) => ({
    id: `term-${year}-${index + 1}`,
    schoolYear,
    name: ["上學期", "下學期"][index] || "第三學期",
    startDate: startDate!,
    endDate: endDate!,
    ...(count === 3 ? { name: `第${index + 1}學期` } : {}),
  }));
}

function validDate(value: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

export function validateAcademicTerms(
  terms: AcademicTerm[],
): "required" | "invalidDate" | "order" | "overlap" | "duplicate" | undefined {
  if (
    !Array.isArray(terms) ||
    terms.some(
      (term) =>
        !term ||
        typeof term.id !== "string" ||
        typeof term.name !== "string" ||
        typeof term.schoolYear !== "string" ||
        typeof term.startDate !== "string" ||
        typeof term.endDate !== "string",
    )
  )
    return "required";
  const ids = new Set<string>();
  const ordered = [...terms].sort((a, b) => a.startDate.localeCompare(b.startDate));
  for (const [index, term] of ordered.entries()) {
    if (!term.id || !term.name.trim() || !term.schoolYear.trim()) return "required";
    if (ids.has(term.id)) return "duplicate";
    ids.add(term.id);
    if (!validDate(term.startDate) || !validDate(term.endDate)) return "invalidDate";
    if (term.startDate > term.endDate) return "order";
    if (index && ordered[index - 1]!.endDate >= term.startDate) return "overlap";
  }
}

export function dateInTerm(date: string, term?: AcademicTerm) {
  return !term || (validDate(date) && date >= term.startDate && date <= term.endDate);
}

export function routineRecordDate(item: StudentRoutineItem) {
  if (item.date) return item.date;
  const date = new Date(item.createdAt);
  return Number.isFinite(date.getTime()) ? new Date(date.getTime() + 8 * 3600_000).toISOString().slice(0, 10) : "";
}

export type TermRecordSource = {
  dailyRecords: DailyRecord[];
  homeworkAssignments: HomeworkAssignment[];
  homeworkRecords: HomeworkRecord[];
  attendanceRecords: AttendanceRecord[];
  homeroomHomeworkRecords: HomeroomHomeworkRecord[];
  homeroomRecords: HomeroomRecord[];
  studentRoutineItems: StudentRoutineItem[];
  calendarEvents: CalendarEvent[];
};

export function scopeStudentRecords<T extends TermRecordSource>(source: T, term?: AcademicTerm): T {
  if (!term) return source;
  const dated = <R extends { date: string }>(records: R[]) => records.filter((record) => dateInTerm(record.date, term));
  const assignments = source.homeworkAssignments.filter((item) => dateInTerm(item.dueDate, term));
  const ids = new Set(assignments.map((item) => item.id));
  return {
    ...source,
    dailyRecords: dated(source.dailyRecords),
    homeworkAssignments: assignments,
    homeworkRecords: source.homeworkRecords.filter((item) => ids.has(item.assignmentId)),
    attendanceRecords: dated(source.attendanceRecords),
    homeroomHomeworkRecords: dated(source.homeroomHomeworkRecords),
    homeroomRecords: dated(source.homeroomRecords),
    studentRoutineItems: source.studentRoutineItems.filter((item) => dateInTerm(routineRecordDate(item), term)),
    calendarEvents: source.calendarEvents.filter(
      (item) => item.date <= term.endDate && (item.endDate || item.date) >= term.startDate,
    ),
  };
}

function latestAttendance(records: AttendanceRecord[], classId: string) {
  const latest = new Map<string, AttendanceRecord>();
  for (const record of records) {
    if (record.classId !== classId) continue;
    const previous = latest.get(record.date);
    if (!previous || record.updatedAt > previous.updatedAt) latest.set(record.date, record);
  }
  return [...latest.values()];
}

export function termStudentSummaries(
  terms: AcademicTerm[],
  classId: string,
  students: Student[],
  source: TermRecordSource,
) {
  // Historical comparisons retain disabled students; current daily entry still uses the active roster.
  const roster = students.map((student) => ({ ...student, active: true }));
  const periods = [...terms]
    .sort((a, b) => a.startDate.localeCompare(b.startDate))
    .map((term) => {
      const records = scopeStudentRecords(source, term);
      const performance = classroomPerformance(classId, roster, records.dailyRecords);
      const grades = gradeOverview(classId, roster, records.homeworkAssignments, records.homeworkRecords);
      const attendance = latestAttendance(records.attendanceRecords, classId);
      const homeroomTasks = records.homeroomHomeworkRecords.filter((record) => record.classId === classId);
      return {
        term,
        rows: roster.map((student) => {
          const daily = performance.students.find((row) => row.student.id === student.id)!;
          const grade = grades.students.find((row) => row.student.id === student.id)!;
          const days = attendance.filter((record) => record.studentIds.includes(student.id));
          const absent = days.filter((record) => record.exceptions[student.id] === "absent").length;
          const late = days.filter((record) => record.exceptions[student.id] === "late").length;
          const subjectRecorded = grade.entries.filter(
            (entry) => entry.record && entry.record.status !== "excused",
          ).length;
          const routine = records.studentRoutineItems.filter((item) => item.classId === classId);
          return {
            term,
            studentId: student.id,
            average: grade.average,
            scored: grade.scored,
            observedDays: daily.history.length,
            answers: daily.history.length ? daily.answers : undefined,
            responseRate: daily.history.length ? daily.answers / daily.history.length : undefined,
            materials: daily.history.length ? daily.missingMaterials : undefined,
            materialRate: daily.history.length ? (daily.missingMaterials / daily.history.length) * 100 : undefined,
            attendanceDays: days.length,
            absent: days.length ? absent : undefined,
            late: days.length ? late : undefined,
            attendanceRate: days.length ? ((days.length - absent) / days.length) * 100 : undefined,
            subjectMissing: subjectRecorded ? grade.missing : undefined,
            subjectRecorded,
            homeroomMissing: homeroomTasks.length
              ? homeroomTasks.filter((record) => record.missingStudentIds.includes(student.id)).length
              : undefined,
            homeroomTasks: homeroomTasks.length,
            routineExceptions: routine.length
              ? routine.filter((item) => item.studentIds.includes(student.id)).length
              : undefined,
          };
        }),
      };
    });
  return [...students].sort(compareStudentNumbers).map((student) => ({
    student,
    periods: periods.map((period) => period.rows.find((row) => row.studentId === student.id)!),
  }));
}
